<template>
  <div class="white-box position-relative">
    <TileLoader :loading="loading"/>
    <div class="title-container">
      <h2>Informations</h2>
      <div class="logos" v-if="recap">
        <img src="/static/icones/lnl.png" :class="!recap.IsLnL ? 'inactive' : ''" title="Ligne et lumière"/>
        <img src="/static/icones/made-in-europe.png" :class="!recap.IsMadeInEurope ? 'inactive' : ''"
             title="Fabriqué en Europe"/>
        <img src="/static/icones/cmem-green.png" :class="!recap.IsCmemGreen ? 'inactive' : ''" :title="'CMEM Green\nEcovadis : ' + (recap.IsEcovadis ? 'Oui' : 'Non') + '\nClassification CMEM : ' + (recap.Classification || 'N/A')"/>
        <img src="/static/icones/bdw.png" :class="!recap.IsBDW ? 'inactive' : ''" title="BDoubleWin"/>
        <img src="/static/icones/frs-gold.png" :class="!recap.IsGold ? 'inactive' : ''" title="Fournisseur Gold"/>
        <div class="scoring" title="Scoring fournisseur">{{ recap.Scoring }}</div>
      </div>
    </div>
    <div class="infos-content-container" v-if="recap">
      <div class="col-1-container">
        <div>
          <p><strong>{{ recap.N1_Code }}</strong> - {{ recap.N2_Label }}</p>
          <p>{{ recap.CountryGathering }}</p>
          <p><a target="_blank" rel="noreferrer" :href="recap.Website">{{ recap.Website }}</a></p>
          <p> Référencé depuis : {{ dayjs(recap.DateReference).year() }} </p>
          <p>
            {{ advanceLabel }} <span class="text-muted" v-if="recap.RfaPaymentFrequency && recap.PropAdvancedRfa">(ont représenté {{
              Math.round(recap.PropAdvancedRfa * 100)
            }}% des RFA en {{ recap.FullDeclarationYear }})</span>
          </p>
          <p class="mt-3 text-success" v-if="recap.IsSimulable">
            Fournisseur simulable ({{recap.SimulationYear}})
          </p>
          <p class="mt-3 text-danger d-flex gap-1" style="font-size: 0.9em;" v-else>
            <DangerIcon style="flex-shrink: 0;"/>
            Fournisseur non simulable ({{recap.SimulationYear}}): {{ recap.SimulableErrors }}
          </p>
        </div>
        <div class="mt-3">
          <a
              :href="getDroneRedirectUrl(`contract-edit/${recap.Guid}/campaign-id/${recap.LastContractCampaignGuid}`)"
              target="_blank">
            <button class="btn-cmem w-100">
              Voir le contrat {{ recap.LastContractYear }} dans le DRONE
            </button>
          </a>
        </div>
      </div>
      <div class="col-2-container">
        <div>
          <div class="number-desc">
            <p>CA {{ recap.FullDeclarationYear }}</p>
            <p>{{ formatEuro(recap.CA, 0) }} (<span :class="recap.ProgCA < 0 ? 'decrease-text' : 'increase-text'">{{
                Math.round(recap.ProgCA * 10000) / 100
              }} %</span>)</p>
          </div>
          <p>
            <strong>{{ recap.NbSocietyInvoiced }}</strong> sociétés facturées en {{ recap.FullDeclarationYear }}
          </p>
          <div class="number-desc">
            <p>Rang CMEM</p>
            <p>{{ recap.RankCMEM }}</p>
          </div>
          <div class="number-desc">
            <p>Rang sous-famille</p>
            <p>{{ recap.RankSubFamily }}</p>
          </div>
          <div v-if="recap.LastStatCA" class="mt-3">
            Dernière stats pdv déclarées à <strong>{{ monthLabel(recap.LastStatMonth) }} {{
              recap.LastStatYear
            }}</strong>
          </div>
          <div v-else class="mt-3 text-danger d-flex align-items-center gap-1">
            <DangerIcon/>
            Aucune stat pdv déclarée
          </div>
          <div v-if="recap.LastStatCA" class="number-desc">
            <p>
              CA Stat pdv
            </p>
            <p>{{ formatEuro(recap.LastStatCA, 0) }} (<span
                :class="recap.ProgLastStatCA < 0 ? 'decrease-text' : 'increase-text'">{{
                Math.round(recap.ProgLastStatCA * 10000) / 100
              }} %</span>)</p>
          </div>

          <div v-if="recap.LastStatGrpCA" class="mt-3">
            Dernière stats grp déclarées à <strong>{{ monthLabel(recap.LastStatGrpMonth) }} {{
              recap.LastStatGrpYear
            }}</strong>
          </div>
          <div v-else class="mt-3 text-muted">
            Aucune stat groupement déclarée
          </div>
          <div v-if="recap.LastStatGrpCA" class="number-desc">
            <p>
              CA Stat grp
            </p>
            <p>{{ formatEuro(recap.LastStatGrpCA, 0) }} (<span
                :class="recap.ProgLastStatGrpCA < 0 ? 'decrease-text' : 'increase-text'">{{
                Math.round(recap.ProgLastStatGrpCA * 10000) / 100
              }} %</span>)</p>
          </div>
        </div>
        <div class="mt-3">
          <a
              :href="mapsUrl"
              target="_blank">
            <button class="btn-cmem w-100">
              Voir les données dans Maps
            </button>
          </a>
        </div>
      </div>
      <div>
        <div class="ellisphere-container">
          <img alt="Logo Cloche Ellisphere" v-if="recap.EllisphereScore === null" src="/static/icones/alert-ellisphere-unknown.png"/>
          <img alt="Logo Cloche Ellisphere" v-else-if="recap.EllisphereScore <= 2" src="/static/icones/alert-ellisphere-rouge.png"/>
          <img alt="Logo Cloche Ellisphere" v-else-if="recap.EllisphereScore <= 5" src="/static/icones/alert-ellisphere-orange.png"/>
          <img alt="Logo Cloche Ellisphere" v-else src="/static/icones/alert-ellisphere.png"/>
          <div class="text-center">
            <p>
              Surveillance financière :
              <strong v-if="recap.EllisphereScore !== null">{{ recap.EllisphereScore }}/10</strong>
              <strong v-else>N/A</strong>
            </p>
            <p class="text-muted">{{ recap.EllisphereRiskClass }}</p>
          </div>
        </div>
        <div class="contact-infos-container">
          <div> Codes CMEM</div>
          <div> {{ recap.CodeTier }} - {{ recap.IdCompta }}</div>
          <div> Adresse</div>
          <div>{{ recap.Address }}<br/><strong>{{ recap.ZipCode }}</strong> {{ recap.City }}</div>
          <div>Téléphone</div>
          <div>{{ recap.Tel }}</div>
          <div>SIRET</div>
          <div>{{ recap.SIRET }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TileLoader from "../../Dashboards/TileLoader.vue";
import dayjs from "dayjs";
import {getDroneRedirectUrl} from "@/helpers/getRedirectUrlHelper";
import DangerIcon from "../../Icons/DangerIcon.vue";

export default {
  name: 'FrsInfosTile',
  methods: {
    getDroneRedirectUrl,
    dayjs,
    formatEuro(value, precision = 2) {
      return Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: precision
      }).format(value);
    },
    monthLabel(value) {
      let labelsPerMonth = {
        1: "fin janvier",
        2: "fin février",
        3: "fin mars",
        4: "fin avril",
        5: "fin mai",
        6: "fin juin",
        7: "fin juillet",
        8: "fin août",
        9: "fin septembre",
        10: "fin octobre",
        11: "fin novembre",
        12: "fin décembre"
      };

      return labelsPerMonth[value];
    }
  },
  components: {DangerIcon, TileLoader},
  props: ["recap", "loading"],
  computed: {
    advanceLabel() {
      if (!this.recap) return "";

      let labels = {
        2: "Semestre",
        3: "Quadrimestre",
        4: "Trimestre",
      }

      if (!this.recap.RfaPaymentFrequency || !labels[this.recap.RfaPaymentFrequency])
        return "Fournisseur sans acomptes";

      return "Fournisseur en acomptes au " + labels[this.recap.RfaPaymentFrequency];
    },
    mapsUrl() {
      let login = new URL(window.location.href).searchParams.get("login");
      let email = new URL(window.location.href).searchParams.get("email");
      let autologinHash = new URL(window.location.href).searchParams.get("autologinHash");
      return 'https://maps.cmem-mat.info/?login=' + login + '&email=' + email + '&autologinHash=' + autologinHash + '&odw=1';
    }
  }
}
</script>

<style scoped>
.white-box {
  padding: 12px;
  /* Peut grandir/rétrécir en fonction des tuiles sur la même ligne, sur une base width: 60em; */
  flex: 1 1 60em;
}

.title-container {
  position: relative;
  margin-bottom: 8px;

  display: flex;
  justify-content: space-between;
}

.title-container::after {
  display: block;
  content: "";
  position: absolute;
  bottom: -8px;
  left: 0;
  width: 100%;
  height: 1px;

  background: linear-gradient(90deg, #FFFFFF 0%, #9F9F9F 100%);
}

.title-container h2 {
  display: flex;
  align-items: center;

  font-size: 20px;
  border-left: 3px solid #A7C13C;
  padding-left: 4px;
}

.title-container .logos {
  display: flex;
  column-gap: 4px;
}

.title-container .logos img {
  width: 42px;
  height: 42px;
  object-fit: contain;
}

.title-container .logos img.inactive {
  opacity: 0.3;
  filter: grayscale(1);
}

.title-container .scoring {
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  line-height: 1;
  border-radius: 50%;
  border: 2px solid #F0A32F;
}

.infos-content-container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  padding: 12px 0 0 0;
}

.infos-content-container p {
  margin: 0;
}

.infos-content-container > div {
  padding: 0 12px;
}

.infos-content-container > div:not(:last-child) {
  border-right: 1px solid #9F9F9F;
}

.infos-content-container .number-desc {
  display: flex;
  justify-content: space-between;
}

.infos-content-container .number-desc > *:nth-child(2) {
  text-align: right;
}

.col-1-container, .col-2-container {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.col-2-container .btn-cmem {
  background-color: #D3E8E3;
}

.ellisphere-container {
  display: flex;
  align-items: center;
  justify-content: center;
  column-gap: 24px;
}

.ellisphere-container > img {
  width: 42px;
  height: 42px;
  object-fit: cover;
}

.contact-infos-container {
  margin-top: 12px;
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: 12px;
}

.increase-text {
  color: #008000;
}

.decrease-text {
  color: #FF0000;
}

</style>



// WEBPACK FOOTER //
// src/components/DashboardCdm/RecapPage/FrsInfosTile.vue